'use client';

import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { TipBubble, type TipAnchor } from './tip-bubble';

/**
 * The shapes both apps are built out of. Each existed three or four times as a
 * `<div>` with a `style={{}}` on it, which is how one app got a figure tile at
 * 23px and the other one at 22px.
 *
 * Deliberately thin: a class from `globals.css` plus the props that class needs,
 * passing the rest through so a page never has to go around the component.
 * Nothing here holds a screen's state.
 */

type Div = React.HTMLAttributes<HTMLDivElement>;

/* ── the label that hovers ──────────────────────────────────────────────── */

/**
 * A tooltip in this product's own voice instead of the browser's `title`, which
 * waits on the OS's clock, uses its font and clips to one line. It also
 * disappears when the row scrolls under `.table-wrap`'s scroll box, so this is a
 * portal to `document.body` anchored to the trigger's coordinates.
 */
export function Tooltip({ label, children }: { label: string; children: React.ReactNode }) {
  const [anchor, setAnchor] = useState<TipAnchor | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);

  /* Where it is, not where to draw: the placing, the window's edges and the
     arrow are `TipBubble`'s, so this and the `title` layer cannot drift into
     two behaviours. */
  function show() {
    const node = triggerRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    setAnchor({ top: rect.top, bottom: rect.bottom, cx: rect.left + rect.width / 2 });
  }

  useEffect(() => {
    if (!anchor) return;
    // A tooltip that stays put while the row it was anchored to scrolls
    // away reads as a stray label pointing at nothing.
    const hide = () => setAnchor(null);
    window.addEventListener('scroll', hide, true);
    return () => window.removeEventListener('scroll', hide, true);
  }, [anchor]);

  return (
    <span
      ref={triggerRef}
      tabIndex={0}
      className="tooltip-host"
      onMouseEnter={show}
      onMouseLeave={() => setAnchor(null)}
      onFocus={show}
      onBlur={() => setAnchor(null)}
    >
      {children}
      {anchor && <TipBubble text={label} anchor={anchor} />}
    </span>
  );
}

/* ── the frame ───────────────────────────────────────────────────────────── */

/**
 * A block of the page with a border around it.
 *
 * `pad` is opt-in because half the cards in this product hold a table, and a
 * table brings its own padding: a padded card around one draws a margin that
 * the rows then have to be indented past.
 */
export function Card({
  pad = false,
  scroll = false,
  className = '',
  children,
  ...rest
}: Div & { pad?: boolean; scroll?: boolean }) {
  const classes = ['card', pad && 'card-pad', scroll && 'table-wrap', className]
    .filter(Boolean)
    .join(' ');
  return (
    <div className={classes} {...rest}>
      {children}
    </div>
  );
}

/**
 * A link out that does not tell the destination where the click came from.
 * `target="_blank"` alone sends a `Referer` naming the exact admin screen, which
 * for a detected leak is the one thing a host must never learn: that an
 * operator's tooling just looked. Both `rel` and `referrerPolicy`, because
 * browsers honour them unevenly, and every outbound link goes through here
 * rather than rebuilding the pair and losing one.
 */
export function ExternalLink({
  href,
  children,
  className = '',
  style,
  onClick,
}: {
  href: string;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      referrerPolicy="no-referrer"
      className={className}
      style={style}
      onClick={onClick}
    >
      {children}
    </a>
  );
}

/** The line inside a card: what it is, and one link out of it. */
export function CardHead({ children, action }: { children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className="card-head">
      <span>{children}</span>
      {action && <span className="card-head-link">{action}</span>}
    </div>
  );
}

/**
 * Blocks across the page.
 *
 * `cols` is how many fit at full width; narrower screens fold them without the
 * caller saying anything, because a page that has to describe its own
 * breakpoints ends up describing them differently on each screen.
 */
export function Grid({
  cols,
  className = '',
  children,
  ...rest
}: Div & { cols: 2 | 3 | 4 | 5 | '2:1' | '1:2' }) {
  const key = { 2: 'g2', 3: 'g3', 4: 'g4', 5: 'g5', '2:1': 'g21', '1:2': 'g12' }[cols];
  return (
    <div className={`grid ${key} ${className}`.trim()} {...rest}>
      {children}
    </div>
  );
}

/** Title, explanation, and the buttons that act on what is below. */
export function RowHead({
  title,
  children,
  actions,
}: {
  title: string;
  children?: React.ReactNode;
  actions?: React.ReactNode;
}) {
  return (
    <div className="rowhead">
      <div>
        <h2>{title}</h2>
        {children && <p>{children}</p>}
      </div>
      <span className="rowhead-spacer" />
      {actions}
    </div>
  );
}

/* ── figures ─────────────────────────────────────────────────────────────── */

/**
 * One number and what it counts.
 *
 * `delta` never renders on its own: it takes the window it was measured over,
 * because a green "+12" with nothing attached is a number nobody can check, and
 * this product's whole argument to a client is that its numbers can be checked.
 */
export function Stat({
  figure,
  label,
  delta,
  className = '',
  children,
  ...rest
}: Div & {
  figure: React.ReactNode;
  label: string;
  delta?: { direction: 'up' | 'down' | 'flat'; text: string };
}) {
  return (
    <div className={`card card-pad stat-tile ${className}`.trim()} {...rest}>
      <div className="n">{figure}</div>
      <div className="l">{label}</div>
      {delta && (
        <div className={`d ${{ up: 'up', down: 'dn', flat: 'flat' }[delta.direction]}`}>
          {delta.text}
        </div>
      )}
      {children}
    </div>
  );
}

/** The tone of the dot beside a fact: what the number is about, in a colour
 *  rather than a repeated word. */
export type StatTone = 'ink' | 'primary' | 'good' | 'muted' | 'warn' | 'bad';

export interface StatFact {
  /** A node, not a string: a count that is somewhere to go carries its own
   *  link, and the band must not know about the app's router. */
  label: React.ReactNode;
  value: React.ReactNode;
  tone?: StatTone;
  /** The sentence the number does not have room for. */
  title?: string;
  key?: string;
}

/* The counts at the head of a screen, as one band: a headline figure, a rail
   of facts, and optionally the one count that is work. Four tiles side by side
   gave every number the same weight and took four times the height. */
/** Two rows of facts, however many there are: a long rail spreads sideways into the free width instead of growing tall. */
function factColumns(count: number): number {
  return count > 6 ? Math.min(Math.ceil(count / 2), 5) : 2;
}

export function StatStrip({
  icon,
  iconTone = 'muted',
  total,
  totalLabel,
  facts,
  alert,
  note,
  plain = false,
  className = '',
  minFigureChars,
}: {
  /** One mark for what the band is about. On the headline only: a glyph beside
   *  every fact as well would be two symbols per row, and each row already
   *  carries a coloured dot for its tone. */
  icon?: React.ReactNode;
  /** What kind of question this band answers, in the same six tones the facts
   *  use. Colour by meaning: a band of numbers is found by its colour before
   *  its heading is read. */
  iconTone?: StatTone;
  total?: React.ReactNode;
  totalLabel?: string;
  facts: readonly StatFact[];
  /** The one count that is work rather than a fact, and the way into it. */
  alert?: { count: React.ReactNode; label: string; title?: string; onClick?: () => void };
  /** One line the figures need and have no room for: what a budget being spent
   *  means, which window a pace was measured over. */
  note?: { text: React.ReactNode; tone?: 'warn' | 'muted' };
  /** Inside a card already: without this the band draws a second one. */
  plain?: boolean;
  className?: string;
  /** Reserves at least this many characters even if this rail's own widest
   *  figure is narrower — pass `statFigureRoom` of every rail on the page so a
   *  number's column starts at the same x in every band, not just its own. */
  minFigureChars?: number;
}) {
  return (
    <div className={`${plain ? '' : 'card '}stat-strip ${className}`.trim()}>
      {total !== undefined && (
        <div className="stat-strip-total">
          <div className="n">
            {icon && (
              <span className={`stat-strip-ico is-${iconTone}`} aria-hidden>
                {icon}
              </span>
            )}
            {total}
          </div>
          {totalLabel && <div className="l">{totalLabel}</div>}
        </div>
      )}
      {total !== undefined && facts.length > 0 && <div className="stat-strip-div" />}
      {facts.length > 0 && (
        /* Every figure in the rail holds the width of the widest of them, so
           the words beside them begin at one x and stay there when a number
           grows from "0" to "$1,240.00". */
        <div
          className="stat-strip-facts"
          style={{ '--fv-ch': Math.max(figureRoom(facts), minFigureChars ?? 0), '--fact-cols': factColumns(facts.length) } as React.CSSProperties}
        >
          {facts.map((fact, index) => (
            <div key={fact.key ?? index} className="stat-strip-fact" title={fact.title}>
              <span className="fl">
                <i className={`fdot fdot-${fact.tone ?? 'muted'}`} aria-hidden />
                {fact.label}
              </span>
              <span className="fv">{fact.value}</span>
            </div>
          ))}
        </div>
      )}
      {note && (
        <>
          <div className="stat-strip-div" />
          <p className={`stat-strip-note${note.tone === 'warn' ? ' is-warn' : ''}`}>{note.text}</p>
        </>
      )}
      {alert && (
        <>
          <div className="stat-strip-div" />
          <button
            type="button"
            className="stat-strip-alert"
            onClick={alert.onClick}
            disabled={!alert.onClick}
            title={alert.title}
          >
            <span className="stat-strip-alert-pip">{alert.count}</span>
            {alert.label}
            {alert.onClick && (
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden>
                <path
                  d="M3.5 8h9M8.5 4l4 4-4 4"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </button>
        </>
      )}
    </div>
  );
}

/** How many characters the widest figure in a rail takes. Nodes that are not
 *  plain text keep the ordinary floor: their width is their own business.
 *  Exported so a page with several `StatStrip`s can compute this once across
 *  all of them and pass it back in as `minFigureChars`, sharing one column
 *  width down the whole page instead of one per band. */
export function figureRoom(facts: readonly StatFact[]): number {
  const lengths = facts.map((fact) =>
    typeof fact.value === 'string' || typeof fact.value === 'number'
      ? String(fact.value).length
      : 3,
  );
  return Math.max(3, ...lengths);
}

/** Label left, value right, one fact per line. */
export function KeyValues({ rows }: { rows: Array<{ k: React.ReactNode; v: React.ReactNode }> }) {
  return (
    <dl className="kv">
      {rows.map((row, index) => (
        <div key={index}>
          <dt className="k">{row.k}</dt>
          <dd className="v">{row.v}</dd>
        </div>
      ))}
    </dl>
  );
}

export interface CheckItem {
  label: React.ReactNode;
  /** `done` ticks, `todo` dashes, `bad` crosses. A dash is not a failure. */
  state: 'done' | 'todo' | 'bad';
  /** The right-hand note: when it happened, or why it has not. */
  note?: React.ReactNode;
}

/** What is finished and what is not, in the order it has to happen. */
export function CheckList({ items }: { items: CheckItem[] }) {
  return (
    <ul className="checklist">
      {items.map((item, index) => (
        <li key={index} className={item.state === 'done' ? '' : item.state}>
          <span>{item.label}</span>
          {item.note && <span className="st">{item.note}</span>}
        </li>
      ))}
    </ul>
  );
}

/**
 * A proportion, drawn.
 *
 * Only where "roughly how much" is the question being asked. A figure the
 * reader wants to read is set as a figure; see `ConfidenceMeter`.
 */
export function Bar({ value, tone }: { value: number; tone?: 'ok' | 'warn' | 'bad' }) {
  const percent = Math.max(0, Math.min(100, Math.round(value * 100)));
  return (
    <div className="bar" role="img" aria-label={`${percent}%`}>
      <i className={tone ?? ''} style={{ width: `${percent}%` }} />
    </div>
  );
}

/** How far through a wizard you are, in segments you can count. */
export function Steps({ total, done }: { total: number; done: number }) {
  return (
    <div className="steps" role="img" aria-label={`Step ${done} of ${total}`}>
      {Array.from({ length: total }, (_, index) => (
        <i key={index} className={index < done ? 'done' : ''} />
      ))}
    </div>
  );
}

/**
 * Where a thumbnail of the leaked material would go, if we kept one.
 *
 * We do not. The hatching is not a placeholder waiting for an image to load,
 * it is the final state, and it means the same thing everywhere it appears.
 */
export function SealedThumb({ label = 'Held under seal' }: { label?: string }) {
  return <span className="thumb" role="img" aria-label={label} title={label} />;
}

/* ── switching a view ────────────────────────────────────────────────────── */

export interface TabItem {
  key: string;
  label: React.ReactNode;
  /** A count riding along the tab: how many rows are behind it. */
  badge?: number;
  /** `attention` (the default) reads as a queue; `neutral` is a plain count
   *  that isn't asking for anything, so it doesn't borrow the warning colour. */
  badgeTone?: 'attention' | 'neutral';
}

/**
 * Pick one of these. Buttons, not links: they change what a page shows about
 * one subject rather than going anywhere, and an address that changes without a
 * navigation is a back button that lies.
 *
 * One control across both apps: a row of small primary buttons reads as three
 * actions rather than one choice with an answer.
 */
export function Tabs({
  items,
  active,
  onSelect,
  label,
}: {
  items: TabItem[];
  active: string;
  onSelect: (key: string) => void;
  /** What the row is for, for anyone who cannot see that it is a row. */
  label: string;
}) {
  return (
    <div className="segmented" role="tablist" aria-label={label}>
      {items.map((item) => (
        <button
          key={item.key}
          type="button"
          role="tab"
          aria-selected={item.key === active}
          onClick={() => onSelect(item.key)}
        >
          {item.label}
          {/* Zero draws nothing. A badge showing 0 is a mark that cannot be
              cleared, and the eye keeps going back to it. */}
          {item.badge ? (
            <span className={`seg-badge ${item.badgeTone === 'neutral' ? 'seg-badge-neutral' : ''}`}>
              {item.badge}
            </span>
          ) : null}
        </button>
      ))}
    </div>
  );
}

/* ── the thing that opens over the page ──────────────────────────────────── */

/**
 * What makes a dialog a dialog, without deciding what it looks like: Escape
 * closes, the backdrop closes, focus moves in and back out, the page behind
 * stops scrolling. Every hand-rolled one here was missing at least two. Separate
 * from {@link Modal} because the shapes differ and the behaviour does not.
 */
export function ModalShell({
  onClose,
  panelClassName,
  backClassName = 'modal-back',
  labelledBy,
  label,
  beside = false,
  children,
}: {
  onClose: () => void;
  /** The panel's own class. It owns its width, padding and background. */
  panelClassName: string;
  /**
   * The backdrop's class, for a dialog that is not centred — a drawer down one
   * side wants the same Escape, the same click-outside and the same scroll
   * lock, and differs only in where the panel is put.
   */
  backClassName?: string;
  /** Id of the element naming this dialog. Supply this or `label`. */
  labelledBy?: string;
  label?: string;
  /**
   * Beside the page rather than over it: the page keeps its scroll and its
   * clicks, so a reader can go straight to the next row and the panel follows.
   * Escape and the panel's own close still shut it; the backdrop does not,
   * because there is nothing to click through to close.
   */
  beside?: boolean;
  children: React.ReactNode;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const opener = useRef<Element | null>(null);
  // onClose is almost always an inline arrow from the caller, so it is a new
  // function every render. Kept in a ref, the focus/escape effect below can
  // depend only on `beside` and run once — before, a parent re-render (a 1s
  // "N ago" tick is enough) changed onClose, re-ran the effect, and
  // `panel.focus()` stole focus from whatever the user was typing in.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    opener.current = document.activeElement;
    panel.current?.focus();

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);

    // The page behind must not scroll while a dialog is over it: on a phone the
    // backdrop scrolls the dialog, and the two together move in opposite
    // directions under one finger. A panel beside the page is the exception —
    // there the page is still being read.
    const previous = document.body.style.overflow;
    if (!beside) document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      if (!beside) document.body.style.overflow = previous;
      // Back to the button that opened it, so a keyboard is not returned to the
      // top of the document every time something is cancelled.
      (opener.current as HTMLElement | null)?.focus?.();
    };
  }, [beside]);

  const isDrawer = /\bdrawer\b/.test(panelClassName);
  useEffect(() => {
    if (isDrawer) restoreDrawerWidth();
  }, [isDrawer]);

  return (
    <div
      className={beside ? `${backClassName} is-beside` : backClassName}
      onMouseDown={(event) => {
        if (beside) return;
        // Down, not click: a drag that starts inside the dialog and ends on the
        // backdrop is a text selection, and as a click handler it closed the
        // dialog and threw the selection away with it.
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={panel}
        className={panelClassName}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        aria-label={labelledBy ? undefined : label}
        tabIndex={-1}
      >
        {isDrawer && <DrawerGrip />}
        {children}
      </div>
    </div>
  );
}

const DRAWER_WIDTH_KEY = 'drawer-w';
const DRAWER_MIN = 360;

/* One width for every drawer, and for the screens that keep room beside one: it
   is the same custom property, so dragging one edge resizes them all, and the
   choice outlives the page. */
function restoreDrawerWidth() {
  try {
    const saved = Number(window.localStorage.getItem(DRAWER_WIDTH_KEY));
    if (saved >= DRAWER_MIN) document.documentElement.style.setProperty('--drawer-w', `${saved}px`);
  } catch {
    /* No storage: the stylesheet's width stands. */
  }
}

/** The drawer's left edge, draggable. Double click puts the width back. */
function DrawerGrip() {
  function start(event: React.PointerEvent<HTMLDivElement>) {
    event.preventDefault();
    const grip = event.currentTarget;
    grip.setPointerCapture(event.pointerId);
    const max = () => Math.max(DRAWER_MIN, window.innerWidth - 120);
    const move = (at: PointerEvent) => {
      const width = Math.min(max(), Math.max(DRAWER_MIN, window.innerWidth - at.clientX));
      document.documentElement.style.setProperty('--drawer-w', `${width}px`);
    };
    const stop = () => {
      grip.removeEventListener('pointermove', move);
      grip.removeEventListener('pointerup', stop);
      try {
        window.localStorage.setItem(DRAWER_WIDTH_KEY, String(Math.round(parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--drawer-w')))));
      } catch {
        /* Not remembered; still resized for this visit. */
      }
    };
    grip.addEventListener('pointermove', move);
    grip.addEventListener('pointerup', stop);
  }

  return (
    <div
      className="drawer-grip"
      role="separator"
      aria-orientation="vertical"
      aria-label="Drag to resize"
      title="Drag to resize, double click to reset"
      onPointerDown={start}
      onDoubleClick={() => {
        document.documentElement.style.removeProperty('--drawer-w');
        try {
          window.localStorage.removeItem(DRAWER_WIDTH_KEY);
        } catch {
          /* Nothing remembered to forget. */
        }
      }}
    />
  );
}

/** The ordinary dialog: a title, a body, and buttons along the bottom. */
export function Modal({
  title,
  sub,
  onClose,
  wide = false,
  full = false,
  tall = false,
  foot,
  children,
}: {
  title: string;
  sub?: React.ReactNode;
  onClose: () => void;
  wide?: boolean;
  /**
   * A ceiling wider than `wide`'s, not a fixed size — the dialog stays
   * `fit-content`, so a short request does not sit in a box built for a big one.
   * For a side-by-side comparison rather than a form; kept apart from `wide`
   * because its other callers are forms that never wanted this much room.
   */
  full?: boolean;
  /**
   * Raises the height ceiling from the ordinary 90vh to 92vh. Content still
   * decides the dialog's actual height (see `.json-well`'s own cap on
   * Search requests' two panes); this only says how much further it's
   * allowed to grow before the *dialog* has to scroll, rather than the
   * panes inside it scrolling on their own.
   */
  tall?: boolean;
  foot?: React.ReactNode;
  children: React.ReactNode;
}) {
  const titleId = useId();
  const panelClassName = ['modal', wide && 'modal-wide', full && 'modal-full', tall && 'modal-tall']
    .filter(Boolean)
    .join(' ');

  return (
    <ModalShell onClose={onClose} panelClassName={panelClassName} labelledBy={titleId}>
      <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
        ×
      </button>
      <h2 id={titleId}>{title}</h2>
      {sub && <p className="modal-sub">{sub}</p>}
      <div className="modal-body">{children}</div>
      {foot && <div className="modal-foot">{foot}</div>}
    </ModalShell>
  );
}
